import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, Ticket, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import InkPhDLogo from '@/components/InkPhDLogo';
import SeoHead from '@/components/SeoHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;
const PLAN_KEYS = ['student', 'researcher', 'doctoral', 'lab'];

/** Admin-only: promo codes for journal-author campaigns, with a redemption report. */
export default function AdminPromoPage() {
  const navigate = useNavigate();
  const user = authStorage.getAuthorUser();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', kind: 'percent', value: 20, plans: [], apply_to: 'first',
    valid_until: '', max_uses: 0, note: '',
  });

  const load = useCallback(async () => {
    if (!user?.id) { navigate('/thesis/login'); return; }
    try {
      const { data } = await axios.get(`${API}/promo-codes`, { params: { user_id: user.id } });
      setCodes(data.codes || []);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error('Could not load the promo codes');
    } finally {
      setLoading(false);
    }
  }, [user?.id, navigate]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/promo-codes`, {
        ...form,
        value: Number(form.value),
        max_uses: Number(form.max_uses) || 0,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      }, { params: { user_id: user.id } });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ ...form, code: '', note: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create the code');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (code) => {
    if (!window.confirm(`Deactivate ${code}? Existing subscriptions are unaffected.`)) return;
    await axios.delete(`${API}/promo-codes/${code}`, { params: { user_id: user.id } });
    toast.success(`${code} deactivated`);
    load();
  };

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div data-testid="promo-admin-denied">
          <p className="text-lg font-semibold mb-2">Admins only</p>
          <Button asChild variant="outline"><Link to="/thesis">Back to my projects</Link></Button>
        </div>
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0e1a] text-foreground thesis-brand">
      <SeoHead title="Promo codes" path="/thesis/admin/promos" noindex
        description="Admin — promo codes and redemption report." />
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/thesis" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="promo-back-link">
            <ArrowLeft className="w-4 h-4" /> Projects
          </Link>
          <InkPhDLogo size="sm" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-10 space-y-6" data-testid="promo-admin-page">
        <div>
          <h1 className="text-4xl sm:text-5xl font-serif font-semibold tracking-tight mb-2"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            Promo <span className="text-[#1d4ed8] dark:text-[#7aa9ff]">codes</span>
          </h1>
          <p className="text-muted-foreground text-base">
            Discounts for authors publishing in your journals — with the redemption report per campaign.
          </p>
        </div>

        <Card data-testid="promo-create-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#1d4ed8]" /> New code
            </CardTitle>
            <CardDescription>
              Percentage codes work on any interval. “Free months” apply to yearly plans (the value
              is converted to the equivalent share of the annual price).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={create} className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="code">Code</Label>
                <Input id="code" required value={form.code} data-testid="promo-code-input"
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="SCIINK25" className="uppercase" />
              </div>
              <div>
                <Label htmlFor="kind">Type</Label>
                <select id="kind" value={form.kind} onChange={set('kind')} data-testid="promo-kind-select"
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="percent">% discount</option>
                  <option value="free_months">Free months (yearly)</option>
                </select>
              </div>
              <div>
                <Label htmlFor="value">{form.kind === 'percent' ? 'Percent off' : 'Free months'}</Label>
                <Input id="value" type="number" min="1" max={form.kind === 'percent' ? 90 : 10}
                  value={form.value} onChange={set('value')} data-testid="promo-value-input" />
              </div>
              <div>
                <Label htmlFor="apply">Applies to</Label>
                <select id="apply" value={form.apply_to} onChange={set('apply_to')}
                  data-testid="promo-applyto-select"
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="first">First payment only</option>
                  <option value="all">Every payment</option>
                </select>
              </div>
              <div>
                <Label htmlFor="until">Valid until</Label>
                <Input id="until" type="date" value={form.valid_until} onChange={set('valid_until')}
                  data-testid="promo-until-input" />
              </div>
              <div>
                <Label htmlFor="uses">Max uses (0 = unlimited)</Label>
                <Input id="uses" type="number" min="0" value={form.max_uses} onChange={set('max_uses')}
                  data-testid="promo-maxuses-input" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="note">Campaign note</Label>
                <Input id="note" value={form.note} onChange={set('note')}
                  placeholder="Authors published in Journal Medica, issue 2/2026"
                  data-testid="promo-note-input" />
              </div>
              <div>
                <Label>Limit to plans</Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {PLAN_KEYS.map((k) => {
                    const on = form.plans.includes(k);
                    return (
                      <button key={k} type="button" data-testid={`promo-plan-${k}`}
                        onClick={() => setForm({
                          ...form,
                          plans: on ? form.plans.filter((p) => p !== k) : [...form.plans, k],
                        })}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                          on ? 'bg-[#0b1a35] text-white border-[#0b1a35]' : 'border-border text-muted-foreground'}`}>
                        {k}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-3">
                <Button type="submit" disabled={saving} data-testid="promo-create-btn"
                  className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ticket className="w-4 h-4 mr-2" />}
                  Create code
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card data-testid="promo-list-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Codes &amp; redemptions ({codes.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" />
              : codes.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="promo-list-empty">
                  No codes yet.
                </p>
              ) : (
                <ul className="divide-y divide-border" data-testid="promo-list">
                  {codes.map((c) => (
                    <li key={c.code} className="py-3" data-testid={`promo-row-${c.code}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono font-semibold">{c.code}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {c.kind === 'percent' ? `${c.value}% off` : `${c.value} months free`}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">
                          {c.apply_to === 'all' ? 'every payment' : 'first payment'}
                        </Badge>
                        {!c.active && <Badge variant="destructive" className="text-[10px]">inactive</Badge>}
                        <span className="text-[12px] text-muted-foreground">
                          {c.uses} use{c.uses === 1 ? '' : 's'}
                          {c.max_uses ? ` / ${c.max_uses}` : ''} · €{c.revenue} collected
                          {c.valid_until ? ` · until ${String(c.valid_until).slice(0, 10)}` : ''}
                          {c.plans?.length ? ` · ${c.plans.join(', ')}` : ''}
                        </span>
                        <div className="flex-1" />
                        {c.active && (
                          <Button size="sm" variant="ghost" className="text-red-600 h-7"
                            onClick={() => deactivate(c.code)} data-testid={`promo-deactivate-${c.code}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" />Deactivate
                          </Button>
                        )}
                      </div>
                      {c.note && <p className="text-[12px] text-muted-foreground mt-1">{c.note}</p>}
                      {c.redemptions?.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                          {c.redemptions.slice(0, 5).map((r, i) => (
                            <li key={i} className="text-[11px] text-muted-foreground font-mono">
                              {String(r.at).slice(0, 16).replace('T', ' ')} · {r.email} · {r.plan}/{r.interval} · €{r.amount}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
